import type { QueryClient } from '@tanstack/react-query';
import { Link, redirect } from '@tanstack/react-router';
import { toast } from 'sonner';
import { getIntegration } from '@logspot-platform/platform-types';
import { PageContent } from '@/components/page-content';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { IntegrationsService } from '@/api/integrations-service';
import { featuresOptions } from '@/hooks/query-options';
import { hasPermission } from '@/lib/permissions';

export type OAuthCallbackSearch = {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
};

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const nameOf = (integrationId: string): string =>
  getIntegration(integrationId)?.name ?? 'The integration';

export const oauthCallbackSearch = (
  search: Record<string, unknown>,
): OAuthCallbackSearch => ({
  code: text(search.code),
  state: text(search.state),
  error: text(search.error),
  error_description: text(search.error_description),
});

export const requireOAuthCallbackAccess = async (context: {
  queryClient: QueryClient;
  userRole?: string;
}): Promise<void> => {
  const features = await context.queryClient.ensureQueryData(featuresOptions());
  if (
    features.integrationsHub !== true ||
    !hasPermission(context.userRole, { organization: ['update'] })
  ) {
    throw redirect({ to: '/' });
  }
};

// The exchange runs in the route loader so it fires once per navigation; success
// leaves the page, so a refresh can't replay it.
export const completeOAuthCallback = async (
  integrationId: string,
  search: OAuthCallbackSearch,
  queryClient: QueryClient,
): Promise<{ failedReason: string }> => {
  const name = nameOf(integrationId);
  const retry = `${name} did not finish connecting. Start again from Integrations.`;
  if (search.error || !search.code || !search.state) {
    return { failedReason: search.error_description ?? retry };
  }
  try {
    await IntegrationsService.crmCallback(integrationId, {
      code: search.code,
      state: search.state,
    });
  } catch (error) {
    return {
      failedReason:
        error instanceof Error && error.message ? error.message : retry,
    };
  }
  await queryClient.invalidateQueries({ queryKey: ['integrations-hub'] });
  toast.success(`${name} connected`);
  // The side layout retains every search param, so the spent code and state are
  // set to undefined (omitting them isn't enough) or they'd stay in the address bar.
  throw redirect({
    to: '/integrations/connected',
    search: (prev) =>
      ({
        ...prev,
        code: undefined,
        state: undefined,
        error: undefined,
        error_description: undefined,
      }) as typeof prev,
  });
};

export function OAuthCallbackFailed({
  integrationId,
  failedReason,
}: {
  integrationId: string;
  failedReason: string;
}) {
  const name = nameOf(integrationId);
  return (
    <>
      <PageHeader title={`Connect ${name}`} />
      <PageContent>
        <Empty className="border">
          <EmptyHeader>
            <EmptyTitle>{name} Not Connected</EmptyTitle>
            <EmptyDescription>{failedReason}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild>
              <Link
                to="/integrations"
                search={(prev: Record<string, unknown>) =>
                  ({
                    ...prev,
                    code: undefined,
                    state: undefined,
                    error: undefined,
                    error_description: undefined,
                  }) as typeof prev
                }
              >
                Back to Integrations
              </Link>
            </Button>
          </EmptyContent>
        </Empty>
      </PageContent>
    </>
  );
}
